import { useQuery } from '@tanstack/react-query';
import { Download, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../contexts/I18nContext';
import { fetchRecordings } from '../../lib/meetApi';

export function formatRecordingDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m}:${s.toString().padStart(2, '0')}`;
}

export const recordingStartedFormat = new Intl.DateTimeFormat('ro-RO', {
  timeZone: 'Europe/Bucharest',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Plays a Meet meeting's recording in a pop-up, over whatever page opened it
 * (the Meet list or the meeting's history page). The `<video>` points at the
 * Range-streaming endpoint, so it starts at once and seeks without pulling the
 * whole multi-GB file. A meeting recorded more than once gets a switcher.
 *
 * Portal + scroll lock + Escape handling follow `ImageLightbox`.
 */
export function MeetRecordingPlayer({
  meetingId,
  title,
  initialRecordingId,
  onClose,
}: {
  meetingId: string;
  title: string;
  initialRecordingId?: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ['meet', 'recordings', meetingId],
    queryFn: () => fetchRecordings(meetingId),
  });
  const ready = (data?.recordings ?? []).filter((r) => r.status === 'READY' && r.streamUrl);
  const [selectedId, setSelectedId] = useState(initialRecordingId);
  const current = ready.find((r) => r.id === selectedId) ?? ready[0];
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [current?.id]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/80 p-2 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${t('meet.recordings.playerTitle')}: ${title}`}
    >
      <div
        className="flex w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{title}</p>
            {current && (
              <p className="text-xs text-muted-foreground">
                {recordingStartedFormat.format(new Date(current.startedAt))}
                {' · '}
                {formatRecordingDuration(current.durationMs)}
              </p>
            )}
          </div>
          {current?.downloadUrl && (
            <a
              href={current.downloadUrl}
              download
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <Download size={15} />
              <span className="hidden sm:inline">{t('meet.recordings.download')}</span>
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('meet.recordings.close')}
            className="inline-flex items-center rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X size={18} />
          </button>
        </div>

        <div className="relative flex aspect-video max-h-[75vh] w-full items-center justify-center bg-black">
          {isLoading ? (
            <Loader2 size={28} className="animate-spin text-fixed-white opacity-70" />
          ) : !current ? (
            <p className="px-6 text-center text-sm text-fixed-white opacity-80">
              {t('meet.recordings.none')}
            </p>
          ) : failed ? (
            <p className="px-6 text-center text-sm text-fixed-white opacity-80">
              {t('meet.recordings.playError')}
            </p>
          ) : (
            <video
              key={current.id}
              src={current.streamUrl ?? undefined}
              controls
              autoPlay
              playsInline
              preload="metadata"
              onError={() => setFailed(true)}
              className="h-full w-full bg-black object-contain"
            />
          )}
        </div>

        {ready.length > 1 && (
          <div className="flex flex-wrap gap-1.5 border-t border-border px-4 py-2.5">
            {ready.map((r, i) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setSelectedId(r.id)}
                className={[
                  'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                  r.id === current?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                ].join(' ')}
              >
                {i + 1}. {recordingStartedFormat.format(new Date(r.startedAt))} ·{' '}
                {formatRecordingDuration(r.durationMs)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
